import { Box, Button, Theme, Typography, useTheme } from "@mui/material"
import React, { useEffect, useState } from "react"
import { ErrorText } from "../../dashboard/chart/CustomeLineChart"
import CustomNavChart from "../../dashboard/chart/CustomNavChart"
import { BondsPerformanceLoader } from "./bondsOverViewLoading"
import { filterDataPointsForNav } from "../../../utils/utilityFunctions"
import { HistoricalNavItem } from "../../../redux-store/types/investorProfile"
import { cloneDeep } from "lodash"
import PerformanceTooltip from "../../dashboard/components/PerformanceTooltip"
import { useHistory } from "react-router"
import { NO_DATA_ERROR_MESSAGE } from "../../../utils/constant"

interface IndNavBtnProps {
    type: string,
    count?: number,
    text: string
}

const navChartButtons = [{
    type: 'month',
    count: 6,
    text: '6M'
}, {
    type: 'year',
    count: 1,
    text: '1Y'
},
{
    type: 'all',
    text: 'Inception'
}]

type PerformanceProps = {
    isLoading: boolean
    getAllData?: {
        data1: Array<[number, number]>,
        data2: Array<[number, number]>,
        data3: Array<[number, number]>,
        data4: Array<[number, number]>,
        data5: Array<[number, number]>,
        label1: string;
        label2: string;
        label3: string;
        label4: string;
        label5: string;
    }
    completeData?: any;
    benchmarkData?: Array<HistoricalNavItem>;

}

interface PerformanceChartData {
    data1?: [number, number][],
    data2?: [number, number][],
    data3?: [number, number][],
    data4?: [number, number][],
    data5?: [number, number][],
    label1?: string;
    label2?: string;
    label3?: string;
    label4?: string;
    label5?: string;
}

const currentDate = new Date();
const cutoffDate = (n: number) => new Date(currentDate.getFullYear() - n, 0, 1);


export default function BondsPerformance(props: PerformanceProps): JSX.Element {
    const history = useHistory();
    const { pathname: pathName, state } = history.location
    const initialRouter = pathName.split('/').filter((item: string) => item !== '')

    const { isLoading, getAllData, completeData, benchmarkData } = props;
    const [navChartBtn, setNavChartBtn] = useState('Inception');
    const { palette } = useTheme()
    const [filteredData, setFilteredData] = useState<Array<[number, number]>>([]);
    const [filteredPerformanceData, setFilteredPerformanceData] = useState<PerformanceChartData>({
        data1: [],
        data2: [],
        data3: [],
        data4: [],
        data5: [],
        label1: "",
        label2: "",
        label3: "",
        label4: "",
        label5: "",
    } as PerformanceChartData)


    useEffect(() => {
        if (getAllData?.data1?.length) setFilteredPerformanceData(cloneDeep({ ...getAllData }));
    }, [getAllData])

    const tooltipData = [
        ...(getAllData?.label1 ? [{
            title: getAllData?.label1 ?? ""
        }] : []),
        ...(initialRouter[0] === 'product' && getAllData?.label2 ? [{
            title: getAllData?.label2 ?? ""
        }] : []),
        ...(initialRouter[0] === 'product' && getAllData?.label3 ? [{
            title: getAllData?.label3 ?? ""
        }] : []),
        ...(initialRouter[0] === 'product' && getAllData?.label4 ? [{
            title: getAllData?.label4 ?? ""
        }] : []),
        ...(initialRouter[0] === 'product' && getAllData?.label5 ? [{
            title: getAllData?.label5 ?? ""
        }] : [])
    ]



    const handleNavChartBtn = (btn: string) => {
        setNavChartBtn(btn);

        if (btn === '6M') {
            const sixMonthsAgo = new Date();
            sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

            setFilteredPerformanceData({
                ...getAllData,
                data1: getAllData?.data1?.filter(([timestamp, _]) => timestamp >= sixMonthsAgo.getTime()) || [],
                data2: getAllData?.data2?.filter(([timestamp, _]) => timestamp >= sixMonthsAgo.getTime()) || [],
                data3: getAllData?.data3?.filter(([timestamp, _]) => timestamp >= sixMonthsAgo.getTime()) || [],
                data4: getAllData?.data4?.filter(([timestamp, _]) => timestamp >= sixMonthsAgo.getTime()) || [],
                data5: getAllData?.data5?.filter(([timestamp, _]) => timestamp >= sixMonthsAgo.getTime()) || [],
            })
        } else if (btn === '1Y') {
            setFilteredPerformanceData({
                ...getAllData,
                data1: getAllData?.data1?.filter(([timestamp, _]) => timestamp >= cutoffDate(1).getTime()) || [],
                data2: getAllData?.data2?.filter(([timestamp, _]) => timestamp >= cutoffDate(1).getTime()) || [],
                data3: getAllData?.data3?.filter(([timestamp, _]) => timestamp >= cutoffDate(1).getTime()) || [],
                data4: getAllData?.data4?.filter(([timestamp, _]) => timestamp >= cutoffDate(1).getTime()) || [],
                data5: getAllData?.data5?.filter(([timestamp, _]) => timestamp >= cutoffDate(1).getTime()) || [],
            })
        } else if (btn === 'Inception') {
            setFilteredPerformanceData(cloneDeep({ ...getAllData }))
        }

    }

    return (
        <Box sx={{
            padding: '25px 30px 45px 30px',
            background: palette.common.white,
            borderRadius: '20px',
            height: '100%',
        }}>
            {isLoading &&
                <BondsPerformanceLoader />
            }
            {(!isLoading && !completeData?.isSuccess) &&
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        height: "100%",
                        position: "relative"
                    }}
                >
                    <Typography
                        variant="h2"
                        sx={(theme: Theme) => ({
                            lineHeight: '25px',
                            letterSpacing: "0.01px",
                            color: theme.palette.text.primary,
                            position: "absolute",
                            top: 0,
                            left: 0
                        })}
                    >
                        Performance
                    </Typography>
                    <Typography
                        variant="body_600"
                        sx={(theme: Theme) => ({
                            fontWeight: 500,
                            lineHeight: "22.5px",
                            letterSpacing: "0.01px",
                            color: theme.palette.text.primary,
                            textAlign: "center",
                            width: "100%"
                        })}
                    >
                        {NO_DATA_ERROR_MESSAGE}
                    </Typography>
                </Box>
            }
            {(!isLoading && (completeData?.isSuccess)) && <>
                <Box sx={(theme: Theme) => ({
                    display: 'flex',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    rowGap: '10px',
                    mb: '55px'
                })}>
                    <Typography variant="h2" color='text.primary'>Portfolio Performance</Typography>
                    <Box sx={(theme: Theme) => ({
                        display: 'flex',
                        // alignItems: 'center',
                        flexDirection: 'row',
                        justifyContent: { sm: 'flex-end', md: 'flex-start' },
                        columnGap: { sm: 0, md: 2, lg: 3 },
                    })}>
                        <Typography
                            component='span'
                            variant="body_500"
                            color="text.primary"
                        >
                            Portfolio Legends
                            <PerformanceTooltip data={tooltipData} />
                        </Typography>

                    </Box>
                </Box>

                <Box>
                    <Box sx={{ height: "400px" }}>
                        {(filteredPerformanceData?.data1?.length === 0 && filteredPerformanceData?.data2?.length === 0 && filteredPerformanceData?.data3?.length === 0 && filteredPerformanceData?.data4?.length === 0 && filteredPerformanceData?.data5?.length === 0) ?
                            <ErrorText /> :
                            <CustomNavChart
                                key={navChartBtn}
                                data={filteredPerformanceData?.data1 || []}
                                data1={initialRouter[0] === 'product' ? filteredPerformanceData?.data2 : []}
                                data2={initialRouter[0] === 'product' ? filteredPerformanceData?.data3 : []}
                                data3={initialRouter[0] === 'product' ? filteredPerformanceData?.data4 : []}
                                data4={initialRouter[0] === 'product' ? filteredPerformanceData?.data5 : []}
                                label1={filteredPerformanceData?.label1 || undefined}
                                label2={(initialRouter[0] === 'product' ? filteredPerformanceData?.label2 : '') || undefined}
                                label3={(initialRouter[0] === 'product' ? filteredPerformanceData?.label3 : '') || undefined}
                                label4={(initialRouter[0] === 'product' ? filteredPerformanceData?.label4 : '') || undefined}
                                label5={(initialRouter[0] === 'product' ? filteredPerformanceData?.label5 : '') || undefined}
                                dynamicY={true}
                            />
                        }
                    </Box>
                    <Box sx={{ mt: '45px', display: 'flex', justifyContent: 'center', alignItems: 'center', columnGap: '10px' }} >
                        {navChartButtons?.map((individualBtn: IndNavBtnProps, index: number) => {
                            return (
                                <Button sx={(theme: Theme) => (
                                    {
                                        ...theme.typography.body_500, letterSpacing: '0.02px', borderRadius: '7px', padding: '5px 15px', border: 'none', cursor: 'pointer', color: { color: navChartBtn === individualBtn?.text ? theme.palette.common.white : theme.palette.text.secondary }, '&:hover': {
                                            border: 'none',
                                        }
                                    }
                                )} variant={navChartBtn === individualBtn?.text ? 'contained' : 'outlined'} key={index}
                                    onClick={() => handleNavChartBtn(individualBtn?.text)}
                                >{individualBtn?.text}</Button>
                            )
                        })}
                    </Box>
                </Box>
            </>}
        </Box>
    )
}